import {
  config,
  BASE_NODE_GAP,
  BASE_LEVEL_HEIGHT,
  isPregnancyNode,
  isOngoingPregnancy,
  isStillbirth,
  isReproSatelliteNode,
  pregnancyLossRenderLabel,
  reproRoleLabelOf,
  donorRoleLabelOf
} from './config.js';
import { labelMaxCharsAt } from './labelText.js';
import {
  getName,
  getAcronym,
  getDisplayUpn,
  reproLabelOn,
  selectPhenotypeRows,
  selectGenotypeRows,
  selectDiagnosesRows,
  selectExternalPatientIdRows,
  getNodeBlockHeight
} from './utils.js';
import { overflowRowText } from './labelValueFilter.js';
import { calculateAgePartial, formatAgeDisplay } from 'src/util/util';
import { partialDateFromBackend } from 'src/util/partialDate';
import { geneMarker } from 'src/util/variantPathogenicity';
import { labelsForNodeAt } from 'src/components/pedigree-chart/labelScope';
import {
  STACKED_LABEL_BLOCKS,
  blockLines,
  genotypeRowText
} from './labelText.js';

// ─── Auto-spacing ────────────────────────────────────────────────────────────
// Derive the GLOBAL node spacing (config.NODE_GAP / config.LEVEL_HEIGHT) from the
// labels each node will actually render, so a label-heavy pedigree spaces itself
// instead of the user hand-tuning the Spacing fields. Pure + deterministic given
// (members, options, measure) — `measure` is injected so jsdom unit tests pass a
// stub (no real canvas measureText in jsdom).
//
// Two passes break the wrap↔width circular dependency (labelMaxChars depends on
// NODE_GAP, which we are computing):
//   Pass A — width → NODE_GAP. Single-line "must fit" labels drive the gap
//     UNCAPPED; wrappable free-text is CAPPED, since it is meant to wrap into
//     extra height rather than force a giant gap. Since TGCLOUD-1781 the only
//     wrappable label left is `personNote` — every clinical record occupies one
//     unwrappable row (name, ids, diagnoses, phenotypes, genotypes, and now the
//     Tests / Diagnosis / Consultation Date blocks too), so they all sit on the
//     uncapped side. Capping a row that cannot wrap would just let it run into
//     the neighbour.
//   Pass B — line count → LEVEL_HEIGHT. At the chosen gap, reserve the exact
//     label-block height via getNodeBlockHeight (the same accumulation the
//     renderer/getLabelPos use), so measured == rendered.
// Both axes floor at BASE, so auto never makes a chart tighter than today's
// default → it can only ever space OUT, never introduce a new overlap.
// ──────────────────────────────────────────────────────────────────────────────

// Tunable constants. The K caps match the manual Spacing control's 500% ceiling
// (CanvasPreferences max:500), so auto is never LESS capable than manual — in
// particular the VERTICAL cap must reach what manual could, or enabling auto on
// a very tall, label-dense node could leave labels overlapping the generation
// below where a manual 400–500% setting would not.
const K_H = 5; // max NODE_GAP = K_H * BASE_NODE_GAP (= horizontal_spacing 500%)
const K_V = 5; // max LEVEL_HEIGHT = K_V * BASE_LEVEL_HEIGHT (= vertical 500%)
const H_MARGIN_SYM = 1.0; // horizontal breathing room = this * SYMBOL_SIZE
const FREE_TEXT_WIDTH_CAP = 1.6 * BASE_NODE_GAP; // wrappable text wraps past this
const V_MARGIN_FS = 1.0; // vertical bottom margin = this * font_size
const NEXT_NODE_CLEARANCE_SYM = 0.8; // room for the next generation's symbol top
// childPath draws the horizontal sibship bar (and the vertical drops from it to
// each child) at the MIDPOINT of the inter-generation gap (midY = (uy+cy)/2).
// A parent's label block hangs straight down from the parent row, so for the
// block to clear that bar/drops the gap must be ~2× the block depth: a parent
// node with tall labels needs LEVEL_HEIGHT >= blockBottom / BAR_GAP_FRACTION.
const BAR_GAP_FRACTION = 0.5;

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
// Round to the nearest whole percent for the read-only Spacing display so it
// faithfully reflects the px actually applied (the chart uses the raw px).
const roundPct = (n) => Math.round(n);

// Symbol size to compute against: prefer the (possibly unsaved) value from the
// settings form so the modal preview tracks symbol-size edits; fall back to the
// live applied singleton (the render path passes options carrying the saved
// value, so both agree there).
const symbolSizeOf = (options) =>
  Number(options?.symbol_size) || Number(config.SYMBOL_SIZE) || 155;

// Offscreen-canvas text measurer, created once. Caches by `${font} ${text}`.
// Returns 0 on any failure (jsdom / no canvas) — callers inject a stub in tests.
export function makeTextMeasurer() {
  let ctx = null;
  let lastFont = null;
  const cache = new Map();
  try {
    ctx = document.createElement('canvas').getContext('2d');
  } catch (_e) {
    ctx = null;
  }
  return function measure(text, font) {
    const str = String(text ?? '');
    if (!str) return 0;
    if (!ctx) return 0;
    const key = `${font} ${str}`;
    const hit = cache.get(key);
    if (hit !== undefined) return hit;
    if (font !== lastFont) {
      ctx.font = font;
      lastFont = font;
    }
    const w = ctx.measureText(str).width || 0;
    cache.set(key, w);
    return w;
  };
}

// Default singleton measurer for the live app.
export const measureTextWidth = makeTextMeasurer();

const labelFont = (options) =>
  `${
    Number(options?.font_size) || 16
  }px Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", sans-serif`;

// The unwrapped label strings a node will render, split by how the renderer
// treats them: `singleLine` (never wrapped → must fit the gap) vs `wrappable`
// (wrapped into lines → width-capped). Mirrors the renderer's text construction
// so the widths track what's drawn.
export function getNodeUnwrappedLines(d, options) {
  // PER-NODE label set — proband-only mode (TGCLOUD-1465) suppresses the
  // identifying labels on everyone but the proband, and the renderer and
  // getLabelPos both scope through this helper. Measuring the UNSCOPED set left
  // a de-identified relative's name / dates / hospital number driving NODE_GAP
  // for a chart that never draws them (TGCLOUD-1610).
  const ls = labelsForNodeAt(
    options?.labelsSet || {},
    d,
    options?.probandPid ?? null
  );
  const fs = Number(options?.font_size) || 16;
  const fmt = ls.formatting;
  // The renderer can bold any label (applyFmt). Bold glyphs are wider, so a
  // bolded row measured at regular weight comes up short. Row-level: if any key
  // on the row is bolded the whole row measures bold — a few px conservative.
  const boldOf = (...keys) => !!(fmt && keys.some((k) => k && fmt[k]?.bold));
  const singleLine = [];
  const wrappable = [];
  // `extra` is ink drawn OUTSIDE the text run on the same row (classification
  // dot, confirmed-diagnosis tick). Rows are centred, so one-sided ink pushes
  // the row's right edge out by that much and the gap has to cover it on both
  // sides — hence callers pass 2x the one-sided reach.
  const pushLine = (arr, s, bold = false, extra = 0) => {
    const t = String(s ?? '').trim();
    if (t) arr.push({ text: t, bold, extra });
  };

  // A de-identified (isHide) node still draws its symbol, acronym, and
  // diagnoses/phenotypes/genotypes, but the renderer BLANKS its name / UPN /
  // dates / hospital number / external IDs / personNote and the stacked blocks
  // (Canvas.jsx guards each of those on `d.isHide`). Mirror that here so we
  // don't reserve width for labels that render empty.
  const deidentified = !!d.isHide;

  pushLine(singleLine, getAcronym(d));
  // Anonymised donor/surrogate role label — drawn INSTEAD of the name.
  const isReproSat = config.ENABLE_REPRO_NODES && isReproSatelliteNode(d);
  if (!deidentified && isReproSat) {
    pushLine(singleLine, reproRoleLabelOf(d), boldOf('donorRole'));
  }
  if (!deidentified) {
    if (
      !isReproSat &&
      ((ls.firstName && d.firstName) || (ls.lastName && d.lastName))
    ) {
      pushLine(
        singleLine,
        getName({ data: d, options }),
        boldOf('firstName', 'lastName')
      );
    }
    // The number the renderer actually draws — `upn`, else the familyMemberId
    // suffix. This used to measure the literal '1' for a proband.
    if (ls.upn) pushLine(singleLine, getDisplayUpn(d), boldOf('upn'));
    if (ls.dob && d.dob) pushLine(singleLine, `DoB: ${d.dob}`, boldOf('dob'));
    // The rendered age string, not a placeholder: a range ("Age 38-42") or an
    // approximation ("Age ~40") is materially wider than "Age 00".
    if (ls.age && d.dob) {
      // Exactly the renderer's construction (Canvas): a range ("Age 38-42") or
      // an approximation ("Age ~40") is materially wider than the "Age 00"
      // placeholder this used to measure.
      const dobPartial = partialDateFromBackend({
        iso: d.dob,
        estimatedFlag: d.estimatedDob,
        precisionField: d.dobPrecision
      });
      const dodPartial = d.dod
        ? partialDateFromBackend({
            iso: d.dod,
            estimatedFlag: d.estimatedDod,
            precisionField: d.dodPrecision
          })
        : null;
      const ageLabel = formatAgeDisplay(
        calculateAgePartial(dobPartial, dodPartial)
      );
      if (ageLabel) pushLine(singleLine, `Age ${ageLabel}`, boldOf('age'));
    }
    if (ls.dod && d.dod) pushLine(singleLine, `DoD: ${d.dod}`, boldOf('dod'));
    if (ls.hospitalNumber && d.hospitalNumber) {
      pushLine(singleLine, String(d.hospitalNumber), boldOf('hospitalNumber'));
    }
    if (ls.externalPatientId) {
      // Same helper the renderer and the row reservation use, so the measured
      // SET is the one drawn. Measuring d.externalPatientIds raw over-reserved
      // when a preset filtered the unit (harmless), but it also never measured
      // the "+ N more" row, and that string can be WIDER than every id it
      // replaces — "+ 8 more" against ids like "A12" — which under-reserves the
      // gap and lets neighbouring labels touch.
      const extSel = selectExternalPatientIdRows(d, ls);
      pushLine(singleLine, overflowRowText(extSel.hiddenCount));
      extSel.items.forEach((id) =>
        pushLine(singleLine, id, boldOf('externalPatientId'))
      );
    }
  }

  // Reproductive labels — free text (karyotype, sex-determination method,
  // fetus name) and a donor role that joins multiple roles with " · ", so these
  // can be the widest thing on the node. Gates mirror computeLabelLayout /
  // the Canvas render filters exactly; all are blanked on a de-identified node.
  if (!deidentified && config.ENABLE_REPRO_NODES) {
    const preg = isPregnancyNode(d) ? d.pregnancy || d.data?.pregnancy : null;
    if (preg && reproLabelOn(ls, 'pregnancyOutcome')) {
      pushLine(
        singleLine,
        pregnancyLossRenderLabel(d),
        boldOf('pregnancyOutcome')
      );
      if (isStillbirth(d)) {
        pushLine(singleLine, 'SB', boldOf('pregnancyOutcome'));
      }
    }
    if (preg && reproLabelOn(ls, 'gestationalAge')) {
      const weeks = String(preg.gestationalWeeks ?? '').trim();
      if (weeks) {
        pushLine(singleLine, `${weeks} weeks`, boldOf('gestationalAge'));
      }
    }
    if (
      preg &&
      reproLabelOn(ls, 'fetalSexMethod') &&
      isOngoingPregnancy(d) &&
      preg.fetalSexKnown
    ) {
      pushLine(
        singleLine,
        preg.sexDeterminationMethod,
        boldOf('fetalSexMethod')
      );
    }
    if (preg && reproLabelOn(ls, 'fetusKaryotype')) {
      pushLine(singleLine, preg.karyotype, boldOf('fetusKaryotype'));
    }
    if (preg && reproLabelOn(ls, 'fetusName')) {
      pushLine(
        singleLine,
        d.fetusName ?? d.data?.fetusName,
        boldOf('fetusName')
      );
    }
    // Not gated on `preg` — a promoted donor is a real person, mirroring the
    // reservation's own note.
    if (reproLabelOn(ls, 'donorRole')) {
      pushLine(singleLine, donorRoleLabelOf(d), boldOf('donorRole'));
    }
  }

  // Diagnoses (with age) — one line per diagnosis, not wrapped by the renderer.
  if (ls.diseaseWithAge) {
    // Same helper the renderer and the row reservation use, so the measured
    // SET of rows is the filtered, sorted one they draw (TGCLOUD-1663). Width
    // is a max over rows, so order is immaterial here — but sharing the helper
    // is what stops the filter drifting between the three passes.
    const dxSel = selectDiagnosesRows(d, ls);
    pushLine(singleLine, overflowRowText(dxSel.hiddenCount));
    dxSel.items.forEach((di) =>
      pushLine(
        singleLine,
        `${di.alias || di.name || ''}${di.age ? ` (${di.age})` : ''}`,
        boldOf('diseaseWithAge'),
        // Clinically-confirmed tick: a circle of r = 0.42fs drawn 52px past
        // the row's right edge (Canvas). Doubled — the row is centred.
        di.clinicallyConfirmed && ls.confirmedDiagnosis
          ? 2 * (52 + 2 * fs * 0.42)
          : 0
      )
    );
  }

  // Phenotypes — name and/or (id).
  if (ls.phenotypesName || ls.phenotypesID) {
    const phenoSel = selectPhenotypeRows(d, ls);
    pushLine(singleLine, overflowRowText(phenoSel.hiddenCount));
    phenoSel.items.forEach((p) => {
      const namePart = ls.phenotypesName ? p.name : '';
      const idPart = ls.phenotypesID ? `(${p.id})` : '';
      // The renderer appends " - Absent" for an unobserved phenotype; include
      // it so the measured width matches what's drawn.
      const absent = p.observed === false ? ' - Absent' : '';
      pushLine(
        singleLine,
        `${namePart}${namePart && idPart ? ' ' : ''}${idPart}${absent}`,
        // The " - Absent" suffix renders bold, so any absent row is a bold row.
        boldOf('phenotypesName', 'phenotypesID') || absent !== ''
      );
    });
  }

  // Genotypes — symbol, (id) and/or carrier status, built by the same helper
  // the renderer draws from, so the carrier suffix ("- Heterozygote", up to
  // "- Homozygote or Compound Heterozygote") is measured rather than clipped.
  if (ls.genotypesName || ls.genotypesID || ls.genotypeCarrierStatus) {
    const genoSel = selectGenotypeRows(d, ls);
    pushLine(singleLine, overflowRowText(genoSel.hiddenCount));
    genoSel.items.forEach((g) => {
      // Classification marker right of the row: a dot of r = max(3, 0.35fs)
      // set 4px past the right edge, plus the reviewed tick (r = 0.34fs, 2px
      // clear) when every source disagreement has been reviewed. Doubled —
      // the row is centred (Canvas).
      const marker = geneMarker(g.variants);
      let markerExtra = 0;
      if (marker) {
        const dotRadius = Math.max(3, fs * 0.35);
        markerExtra = 2 * (4 + 2 * dotRadius);
        if (marker.allDisagreementsReviewed) {
          markerExtra += 2 * (2 + 2 * fs * 0.34);
        }
      }
      pushLine(
        singleLine,
        genotypeRowText(g, ls),
        boldOf('genotypesName', 'genotypesID', 'genotypeCarrierStatus'),
        markerExtra
      );
    });
  }

  // Stacked blocks (header is short; items are the record text). The renderer
  // blanks these on de-identified nodes (Canvas `!d.isHide` filter), so skip
  // them here too — otherwise we'd reserve gap width for text that node never
  // draws.
  //
  // These rows go in `singleLine`, NOT `wrappable` (TGCLOUD-1781). They were
  // wrappable while a long record could break across rows: capping their width
  // contribution at FREE_TEXT_WIDTH_CAP was safe precisely because the overflow
  // turned into height instead. A record now occupies exactly one row whatever
  // the gap, so a capped contribution would leave that row running into the
  // neighbour — the gap has to grow to fit it. `personNote` stays wrappable
  // below: it is the one label that still wraps.
  if (!deidentified) {
    // EVERY stacked block is measured through `blockLines` — including the
    // composed Tests block, which used to be special-cased through
    // `buildTestItems` here. That special case predated blockLines handling the
    // Tests block, and by TGCLOUD-1792 it had become the same defect twice over:
    // `buildTestItems` returns the kept RECORDS and nothing else, so the
    // "+ N more" overflow row was never measured (and that row can be the widest
    // thing on the node — "+ 11 more" against short panel names), and a block
    // whose filter hid every record returns no items at all, so `continue` left
    // the header the renderer still draws unmeasured too.
    //
    // blockLines is what the Canvas renderer draws and what computeLabelLayout
    // counts for the reservation, so measuring it is what keeps all three passes
    // on one list.
    for (const blk of STACKED_LABEL_BLOCKS) {
      const lines = blockLines(blk, d, ls);
      if (!lines.length) continue;
      lines.forEach((ln) => {
        const runs = ln.runs || [];
        pushLine(
          singleLine,
          runs.map((run) => run.text).join(''),
          // PER-RUN, not per-block: a Tests row carries one run per field, so
          // bolding Test Result alone still has to measure that row bold. The
          // plain blocks have a single run keyed by the block, which is exactly
          // what boldOf(blk.key) used to say. The overflow row's run is keyed
          // null and boldOf ignores it.
          boldOf(...runs.map((run) => run.key))
        );
      });
    }
    // Blanked on a de-identified node like every other free-text label.
    if (ls.personNote && d.personNote) {
      pushLine(wrappable, d.personNote, boldOf('personNote'));
    }
  }

  return { singleLine, wrappable };
}

// Pass A: the global NODE_GAP needed so adjacent (centred) labels don't overlap.
export function computeTargetNodeGap(members, options, measure) {
  const font = labelFont(options);
  const sym = symbolSizeOf(options);
  let maxWidth = 0;
  for (const d of members || []) {
    if (!d || d.hidden) continue;
    const { singleLine, wrappable } = getNodeUnwrappedLines(d, options);
    const boldFont = `bold ${font}`;
    let nodeWidth = 0;
    for (const e of singleLine) {
      nodeWidth = Math.max(
        nodeWidth,
        measure(e.text, e.bold ? boldFont : font) + e.extra
      );
    }
    for (const e of wrappable) {
      // The cap applies to the TEXT — wrappable blocks carry no side ink.
      nodeWidth = Math.max(
        nodeWidth,
        Math.min(measure(e.text, e.bold ? boldFont : font), FREE_TEXT_WIDTH_CAP)
      );
    }
    maxWidth = Math.max(maxWidth, nodeWidth);
  }
  return clamp(
    maxWidth + H_MARGIN_SYM * sym,
    BASE_NODE_GAP,
    K_H * BASE_NODE_GAP
  );
}

// Whether a member has descendants — i.e. a child-connector (sibship bar +
// drops) is drawn in the gap directly below it. Such a node's label block must
// clear the mid-gap bar; a leaf's need only clear the next generation's nodes.
function buildHasChildren(members) {
  const parentPids = new Set();
  for (const m of members || []) {
    if (m?.fatherId != null) parentPids.add(String(m.fatherId));
    if (m?.motherId != null) parentPids.add(String(m.motherId));
  }
  return (m) =>
    (Array.isArray(m?.childrenIds) && m.childrenIds.length > 0) ||
    (m?.pid != null && parentPids.has(String(m.pid)));
}

// Pass B: tallest label-block at the chosen gap. Returns the global max (every
// node's labels must clear the next generation's NODES) and the max over nodes
// WITH children (those also have a sibship bar at the gap midpoint to clear).
export function computeMaxBlockHeight(members, options, nodeGapPx) {
  const sym = symbolSizeOf(options);
  const maxChars = labelMaxCharsAt(nodeGapPx, sym, options?.font_size);
  const hasChildren = buildHasChildren(members);
  let maxBottom = 0;
  let maxBottomWithChildren = 0;
  for (const d of members || []) {
    if (!d || d.hidden) continue;
    const bottom = getNodeBlockHeight(d, options, maxChars, sym);
    maxBottom = Math.max(maxBottom, bottom);
    if (hasChildren(d)) {
      maxBottomWithChildren = Math.max(maxBottomWithChildren, bottom);
    }
  }
  return { maxBottom, maxBottomWithChildren };
}

// The vertical space ONE node's labels need below its row, pre-clamped to the
// same [BASE, K_V * BASE] window as the global scalar (TGCLOUD-1610).
//
// This is Pass B's formula applied per node instead of to a global max. That
// substitution is exact, not an approximation: max(x + C) == max(x) + C, x/0.5
// is monotone, and clamp is monotone — so the max of these needs over ANY set
// of nodes equals the scalar that same set would have produced. The engine can
// therefore take the max over one generation and get a locally correct gap,
// while the whole-chart max stays exactly today's levelHeightPx.
//
// Keyed by String(pid): pid is the engine's identity for a person, and proxies
// carry one. Derived reproductive nodes are absent from `members`, so they
// simply contribute no need and their row falls back to the BASE floor —
// unchanged from today, where their labels are unmeasured either way.
export function computePerNodeVerticalNeeds(members, options, nodeGapPx) {
  const sym = symbolSizeOf(options);
  const fs = Number(options?.font_size) || 16;
  const maxChars = labelMaxCharsAt(nodeGapPx, sym, options?.font_size);
  const hasChildren = buildHasChildren(members);
  const needByPid = {};
  for (const d of members || []) {
    if (!d || d.hidden || d.pid == null) continue;
    const bottom = getNodeBlockHeight(d, options, maxChars, sym);
    const need = Math.max(
      bottom + NEXT_NODE_CLEARANCE_SYM * sym + V_MARGIN_FS * fs,
      hasChildren(d) ? (bottom + V_MARGIN_FS * fs) / BAR_GAP_FRACTION : 0
    );
    needByPid[String(d.pid)] = clamp(
      need,
      BASE_LEVEL_HEIGHT,
      K_V * BASE_LEVEL_HEIGHT
    );
  }
  return needByPid;
}

// Main entry. Returns the derived pixel gaps (set straight onto config) plus the
// equivalent percentages (for the read-only Spacing fields in the settings UI).
export function computeAutoSpacing(
  members,
  options,
  measure = measureTextWidth
) {
  const sym = symbolSizeOf(options);
  const fs = Number(options?.font_size) || 16;

  const nodeGapPx = computeTargetNodeGap(members, options, measure);
  const { maxBottom, maxBottomWithChildren } = computeMaxBlockHeight(
    members,
    options,
    nodeGapPx
  );
  // (a) every node's labels clear the NEXT generation's nodes;
  // (b) a node WITH children must also clear the sibship bar/drops at the gap
  //     midpoint, so the gap is ~block / BAR_GAP_FRACTION (≈ 2× the block).
  const levelForNextNode =
    maxBottom + NEXT_NODE_CLEARANCE_SYM * sym + V_MARGIN_FS * fs;
  const levelForSibBar =
    maxBottomWithChildren > 0
      ? (maxBottomWithChildren + V_MARGIN_FS * fs) / BAR_GAP_FRACTION
      : 0;
  const levelHeightPx = clamp(
    Math.max(levelForNextNode, levelForSibBar),
    BASE_LEVEL_HEIGHT,
    K_V * BASE_LEVEL_HEIGHT
  );

  // Per-generation spacing needs the same measurement broken down per node, so
  // the engine — which is the only thing that knows which generation a node is
  // in — can take the max per row. `levelHeightPx` above stays exactly the max
  // of these, and remains what a uniform layout uses.
  const rowNeeds = config.ENABLE_PER_GENERATION_LEVEL_HEIGHT
    ? computePerNodeVerticalNeeds(members, options, nodeGapPx)
    : null;

  return {
    nodeGapPx,
    levelHeightPx,
    rowNeeds,
    horizontalSpacingPct: roundPct((nodeGapPx / BASE_NODE_GAP) * 100),
    verticalSpacingPct: roundPct((levelHeightPx / BASE_LEVEL_HEIGHT) * 100)
  };
}
